import { theme } from '@policygenius/mortar';
import styled, { css } from 'styled-components';
import { fontWeight, suisseIntl, victorSerif } from '../typography';

export const Section = styled.section`
  width: 100%;
  padding: 24px 16px;
  position: relative;

  @media (min-width: ${theme.breakpoints.medium}px) {
    padding: 40px 48px 8px;
  }
`;

export const SectionBackground = styled.div`
  background-color: #f7f5f3;
  border-radius: 12px;
  padding: 48px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  overflow: hidden;

  @media (min-width: ${theme.breakpoints.medium}px) {
    gap: 48px;
  }
`;

export const TitleSection = styled.div`
  width: 100%;
  max-width: 782px;
  text-align: center;
`;

export const Title = styled.h2`
  ${victorSerif()};
  margin: 0 0 20px;
  color: #131212;
  font-size: 34px;
  line-height: 1.1;
  letter-spacing: 0;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 64px;
    margin-bottom: 20px;
  }
`;

export const EasyWrapper = styled.span`
  display: inline-block;
  position: relative;
`;

export const EasyText = styled.em`
  font-style: italic;
`;

export const TitleUnderlineWrap = styled.span`
  position: absolute;
  left: 0;
  bottom: -6px;
  width: 64px;
  height: 11px;
  line-height: 0;
  pointer-events: none;
  overflow: visible;

  @media (min-width: ${theme.breakpoints.medium}px) {
    bottom: -8px;
    width: 134px;
    height: 16px;
  }
`;

export const TitleUnderlineSvg = styled.svg<{ $isVisible: boolean }>`
  display: block;
  width: 100%;
  height: 100%;

  path {
    stroke-dasharray: 2000;
    stroke-dashoffset: 2000;
    transition: stroke-dashoffset 2s ease-in-out;
  }

  ${({ $isVisible }) =>
    $isVisible &&
    css`
      path {
        stroke-dashoffset: 0;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    path {
      stroke-dashoffset: 0;
      transition: none;
    }
  }
`;

export const Subtitle = styled.p`
  ${suisseIntl(fontWeight.regular)};
  margin: 0 auto;
  color: #131212;
  font-size: 18px;
  line-height: 1.3;
  max-width: 100%;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 24px;
  }
`;

export const StepsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 22px;
  justify-items: center;

  @media (min-width: ${theme.breakpoints.medium}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 40px;
    max-width: 1190px;
    padding: 0 16px;
  }

  @media (min-width: ${theme.breakpoints.medium}px) and (max-width: 1175px) {
    & > *:nth-child(3) {
      grid-column: 1 / -1;
      justify-self: center;
    }
  }

  @media (min-width: 1176px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    & > *:nth-child(3) {
      grid-column: auto;
      justify-self: stretch;
    }
  }
`;

export const StepCard = styled.article`
  background-color: #ffffff;
  border-radius: 13px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 13px;
  width: 100%;
  max-width: 370px;
  min-height: 411px;
  margin: 0 auto;
`;

export const CardIcon = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
    max-width: 245px;
  }
`;

export const StepNumber = styled.div`
  width: 41px;
  height: 41px;
  background-color: #131212;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${suisseIntl(fontWeight.medium)};
  font-size: 16px;
  line-height: 1.3;
  color: #ffffff;
`;

export const CardTitle = styled.h3`
  ${victorSerif()};
  margin: 0;
  color: #131212;
  font-size: 26px;
  line-height: 1;

  @media (min-width: ${theme.breakpoints.medium}px) {
    font-size: 30px;
  }
`;

export const CardBody = styled.div`
  ${suisseIntl(fontWeight.regular)};
  font-size: 16px;
  line-height: 1.3;
  color: #131212;

  p {
    margin: 0;
  }
`;

export const StepEmphasis = styled.span`
  font-weight: 700;
`;

export const IconDrawPath = styled.path<{ $isVisible: boolean }>`
  stroke-dasharray: 2000;
  stroke-dashoffset: 2000;
  transition: stroke-dashoffset 2s ease-in-out;

  ${({ $isVisible }) =>
    $isVisible &&
    css`
      stroke-dashoffset: 0;
    `}

  @media (prefers-reduced-motion: reduce) {
    stroke-dashoffset: 0;
    transition: none;
  }
`;

export const CtaContainer = styled.div`
  display: flex;
  justify-content: center;
`;

export const CtaButton = styled.button`
  margin: 0;
  padding: 14px 20px;
  border: none;
  border-radius: 5px;
  ${suisseIntl(fontWeight.medium)};
  font-size: 16px;
  line-height: 1.3;
  color: #131212;
  background-color: #eb6424;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  -webkit-appearance: none;

  &:hover {
    background-color: #d85a20;
  }

  &:focus-visible {
    outline: 2px solid #131212;
    outline-offset: 2px;
  }
`;

export const CtaButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
